import type { JSX } from 'react';
import { useState } from 'react';
import { Alert, Button, List, Modal, PasswordInput, Stack, Text } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { ApiError, api } from '../api';

/**
 * Changing one's own password.
 *
 * The rules are stated up front rather than discovered by trial: nobody should
 * have to guess what a form wants. They match the server's policy, which is the
 * one that actually decides; the check here only saves a round trip.
 */
const MIN_LENGTH = 10;

export function ChangePasswordModal({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}): JSX.Element {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');

  const change = useMutation({
    mutationFn: () => api.changePassword({ currentPassword: current, newPassword: next }),
  });

  const reset = (): void => {
    setCurrent('');
    setNext('');
    setRepeat('');
    change.reset();
  };

  const close = (): void => {
    reset();
    onClose();
  };

  const tooShort = [...next].length > 0 && [...next].length < MIN_LENGTH;
  const mismatch = repeat.length > 0 && next !== repeat;
  const ready =
    current.length > 0 && [...next].length >= MIN_LENGTH && next === repeat && !change.isPending;

  return (
    <Modal opened={opened} onClose={close} title="Passwort ändern" size="md">
      {change.isSuccess ? (
        <Stack>
          <Alert color="green" title="Passwort geändert">
            Das neue Passwort gilt ab sofort.
            {change.data.revokedSessions > 0 &&
              ` ${change.data.revokedSessions} andere Sitzung${
                change.data.revokedSessions === 1 ? ' wurde' : 'en wurden'
              } beendet.`}
          </Alert>
          <Button onClick={close}>Schließen</Button>
        </Stack>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (ready) change.mutate();
          }}
        >
          <Stack>
            {change.isError && (
              <Alert color="red">
                {change.error instanceof ApiError
                  ? change.error.message
                  : 'Das Passwort konnte nicht geändert werden.'}
              </Alert>
            )}

            <PasswordInput
              label="Bisheriges Passwort"
              autoComplete="current-password"
              required
              value={current}
              onChange={(event) => setCurrent(event.currentTarget.value)}
            />
            <PasswordInput
              label="Neues Passwort"
              autoComplete="new-password"
              required
              value={next}
              error={tooShort ? `Mindestens ${MIN_LENGTH} Zeichen` : undefined}
              onChange={(event) => setNext(event.currentTarget.value)}
            />
            <PasswordInput
              label="Neues Passwort wiederholen"
              autoComplete="new-password"
              required
              value={repeat}
              error={mismatch ? 'Die beiden Eingaben stimmen nicht überein' : undefined}
              onChange={(event) => setRepeat(event.currentTarget.value)}
            />

            <Text size="xs" c="dimmed">
              Maßgeblich ist die Länge, nicht Sonderzeichen. Ein Satz aus
              mehreren Wörtern ist sicherer und leichter zu merken als ein kurzes
              Kunstwort.
            </Text>
            <List size="xs" c="dimmed" spacing={2}>
              <List.Item>mindestens {MIN_LENGTH} Zeichen</List.Item>
              <List.Item>anders als das bisherige Passwort</List.Item>
              <List.Item>nicht die eigene E-Mail-Adresse</List.Item>
            </List>
            <Text size="xs" c="dimmed">
              Alle anderen Sitzungen dieses Kontos werden beim Ändern beendet.
            </Text>

            <Button type="submit" disabled={!ready} loading={change.isPending}>
              Passwort ändern
            </Button>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
